/**
 * Shared Pricing Constants for ZiBuzz Subscription
 * Synced with Ziniz website pricing data
 *
 * This file centralizes all pricing information to ensure consistency
 * between the Ziniz marketing website and ZiBuzz application.
 *
 * Exports are organized by feature/page for better code organization:
 * - SUBSCRIPTION_PRICING: For SubscriptionModal component
 * - SUBSCRIPTION_PAGE_PRICING: For Subscription.js page component
 * - BILLING: For billing cycle related constants
 * - PLANS: For plan tier and name constants
 */

// ========== PLAN TIER IDENTIFIERS ==========

export const PLAN_TIERS = {
  SOLO: 'solo',
  GALAXY: 'galaxy',
  TRIAL: 'trial',
};

// Plan display names
export const PLAN_NAMES = {
  [PLAN_TIERS.SOLO]: 'Solo',
  [PLAN_TIERS.GALAXY]: 'Galaxy',
  [PLAN_TIERS.TRIAL]: 'Trial',
};

// ========== BILLING CYCLE CONFIGURATION ==========

// Billing cycle configurations with discounts
export const BILLING_CYCLES = {
  MONTHLY: {
    key: 'monthly',
    label: 'Monthly',
    discount: 0,
    multiplier: 1,
  },
  QUARTERLY: {
    key: 'quarterly',
    label: 'Quarterly',
    discount: 2,
    multiplier: 3,
  },
  ANNUAL: {
    key: 'annual',
    label: 'Annual',
    discount: 5,
    multiplier: 12,
  },
};

// ========== PRICING DATA ==========

// Comprehensive pricing data for all plans
const PRICING_DATA = {
  [PLAN_TIERS.SOLO]: {
    tier: PLAN_TIERS.SOLO,
    name: PLAN_NAMES[PLAN_TIERS.SOLO],
    badge: null,
    priceDefault: 799,
    priceOffer: 499,
    pricePeriod: '/mo',
    description: 'Perfect for founders and small businesses managing WhatsApp leads.',
    features: [
      '1 WhatsApp Business account',
      'Only Admin access',
      'Lead tracking',
    ],
    specialOffer: false,
    offerLabel: '₹500 OFF today',
    offerDiscount: 500,
    offerDiscountType: 'fixed', // 'fixed' or 'percent'
    trialAvailable: true,
    planHierarchy: 1,
  },
  [PLAN_TIERS.GALAXY]: {
    tier: PLAN_TIERS.GALAXY,
    name: PLAN_NAMES[PLAN_TIERS.GALAXY],
    badge: '⭐ MOST POPULAR',
    priceDefault: 1999,
    priceOffer: 1399,
    pricePeriod: '/mo',
    description: 'Built for growing teams handling larger conversation volumes.',
    features: [
      'All in Solo Plan, plus:',
      '4 team members (better deal for bigger teams)',
      'Shared inbox',
      'Team collaboration',
      'Priority support',
    ],
    specialOffer: false,
    offerLabel: '₹1000 OFF today',
    offerDiscount: 1000,
    offerDiscountType: 'fixed', // 'fixed' or 'percent'
    trialAvailable: true,
    planHierarchy: 2,
  },
};

/**
 * Calculate pricing for a given plan and billing cycle
 *
 * @param {string} tier - Plan tier (solo, galaxy)
 * @param {string} billingCycle - Billing cycle (monthly, quarterly, annual)
 * @param {boolean} useOffer - Whether to use offer price (default: true)
 * @returns {Object|null} Pricing breakdown or null if invalid tier
 */
export const calculatePricing = (tier, billingCycle, useOffer = true) => {
  const plan = PRICING_DATA[tier];
  if (!plan) return null;

  // Find the billing cycle config
  const cycleKey = billingCycle.toUpperCase();
  const cycle = BILLING_CYCLES[cycleKey];

  if (!cycle) {
    console.warn(`Invalid billing cycle: ${billingCycle}`);
    return null;
  }

  // Determine base monthly price (offer or default)
  const monthlyPrice = useOffer && plan.priceOffer ? plan.priceOffer : plan.priceDefault;

  // Calculate billing cycle total
  const billingCycleTotal = monthlyPrice * cycle.multiplier;

  // Calculate billing cycle savings
  const billingCycleSavings = (billingCycleTotal * cycle.discount) / 100;

  // Total after billing cycle discount
  const totalAfterCycleSavings = billingCycleTotal - billingCycleSavings;

  // Apply special offer discount if applicable
  let specialOfferAmount = 0;
  if (plan.specialOffer) {
    if (plan.offerDiscountType === 'percent') {
      specialOfferAmount = (totalAfterCycleSavings * plan.offerDiscount) / 100;
    } else {
      specialOfferAmount = plan.offerDiscount;
    }
  }
  const finalTotal = totalAfterCycleSavings - specialOfferAmount;

  // Calculate original total using default price (for comparison)
  const originalMonthlyPrice = plan.priceDefault;
  const originalBillingCycleTotal = originalMonthlyPrice * cycle.multiplier;
  const originalTotalAfterCycleSavings =
    originalBillingCycleTotal - (originalBillingCycleTotal * cycle.discount) / 100;
  const originalFinalTotal = originalTotalAfterCycleSavings - specialOfferAmount;

  return {
    // Base pricing
    monthlyPrice,
    defaultMonthlyPrice: plan.priceDefault,

    // Billing cycle calculations
    billingCycleTotal,
    billingCycleSavings,
    totalAfterCycleSavings,

    // Special offer
    specialOfferAmount,

    // Final amounts
    finalTotal: Math.round(finalTotal),
    originalTotal: Math.round(originalFinalTotal),

    // Metadata
    hasOffer: plan.priceOffer !== null && plan.priceOffer !== undefined,
    cycleLabel: cycle.label,
    cycleDiscount: cycle.discount,
    cycleMultiplier: cycle.multiplier,
  };
};

/**
 * Get billing cycle options as array for dropdowns/selectors
 * @returns {Array} Array of billing cycle options
 */
export const getBillingCycleOptions = () => {
  return Object.values(BILLING_CYCLES);
};

/**
 * Get plan options as array for dropdowns/selectors
 * @returns {Array} Array of plan options
 */
export const getPlanOptions = () => {
  return Object.keys(PRICING_DATA).map((tier) => ({
    value: tier,
    label: PRICING_DATA[tier].name,
  }));
};

/**
 * Format currency amount in Indian Rupee format
 * @param {number} amount - Amount to format
 * @returns {string} Formatted amount (e.g., "₹1,499")
 */
export const formatCurrency = (amount) => {
  return `₹${Math.round(amount).toLocaleString('en-IN')}`;
};

// Export PRICING_DATA for direct access
export { PRICING_DATA };

// ========== FEATURE-SPECIFIC EXPORTS ==========

/**
 * Subscription Modal specific exports
 * Used in: src/components/SubscriptionModal.js
 */
export const SUBSCRIPTION_MODAL = {
  PLAN_TIERS,
  PLAN_NAMES,
  PRICING_DATA,
  BILLING_CYCLES,
  calculatePricing,
  formatCurrency,
};

/**
 * Subscription Page specific exports
 * Used in: src/pages/Subscription.js
 */
export const SUBSCRIPTION_PAGE = {
  PLAN_TIERS,
  PLAN_NAMES,
  PRICING_DATA,
  BILLING_CYCLES,
  calculatePricing,
  formatCurrency,
  getBillingCycleOptions,
  getPlanOptions,
};

/**
 * Billing & Payment specific exports
 * Used across billing-related components
 */
export const BILLING = {
  BILLING_CYCLES,
  getBillingCycleOptions,
  calculatePricing,
  formatCurrency,
};

/**
 * Plan Management specific exports
 * Used for plan display and management
 */
export const PLANS = {
  PLAN_TIERS,
  PLAN_NAMES,
  PRICING_DATA,
  getPlanOptions,
};
